import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Save, X, Upload, LogOut, 
  Settings, Image, Award, User, Key, Home, Eye,
  Heart, MessageCircle, Calendar, TrendingUp,
  Activity, BarChart3, Loader
} from 'lucide-react';
import './ArtistDashboard.css';
import ArtistExhibition from './ArtistExhibition';
import ArtistBlog from './ArtistBlog';

const API_URL = 'https://my-musing-backend.onrender.com/api';

const ArtistDashboard = () => {
  const [activeTab, setActiveTab] = useState('artworks');
  const [artworks, setArtworks] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [genres, setGenres] = useState([]);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [exhibitions, setExhibitions] = useState([]);
  const [blogs, setBlogs] = useState([]);
  
  // Pagination states
  const [artworksPage, setArtworksPage] = useState(1);
  const [achievementsPage, setAchievementsPage] = useState(1);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  
  // Store scroll position and pagination state
  const scrollPositionRef = useRef(0);
  const savedPaginationRef = useRef({
    artworksPage: 1,
    achievementsPage: 1
  });
  
  const [artworkForm, setArtworkForm] = useState({
    title: '', description: '', genres: [], images: []
  });
  const [achievementForm, setAchievementForm] = useState({
    title: '', description: '', image: null, hasImage: false
  });
  const [profileForm, setProfileForm] = useState({
    name: '', bio: '', designation: '', instagram: '', facebook: '', photo: null
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });
  const [newGenre, setNewGenre] = useState('');

  const navigate = useNavigate();

  // Viewport detection for responsive pagination
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  useEffect(() => {
    let resizeTimeout;
    const updateViewport = () => {
      const width = window.innerWidth;
      setViewport({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateViewport, 150);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Items per page based on viewport
  const itemsPerPage = useMemo(() => ({
    artworks: {
      mobile: 6,
      tablet: 9,
      desktop: 12
    },
    achievements: {
      mobile: 4,
      tablet: 6,
      desktop: 9
    }
  }), []);

  const getItemsPerPage = useCallback((type) => {
    const config = itemsPerPage[type];
    if (viewport.isMobile) return config.mobile;
    if (viewport.isTablet) return config.tablet;
    return config.desktop;
  }, [viewport.isMobile, viewport.isTablet, itemsPerPage]);

  // Displayed artworks with pagination
  const displayedArtworks = useMemo(() => {
    const itemsCount = artworksPage * getItemsPerPage('artworks');
    return artworks.slice(0, itemsCount);
  }, [artworks, artworksPage, getItemsPerPage]);

  // Separated achievements with pagination
  const textAchievements = useMemo(() => 
    achievements.filter(a => !a.imageUrl || a.imageUrl === ""),
    [achievements]
  );

  const imageAchievements = useMemo(() => 
    achievements.filter(a => a.imageUrl && a.imageUrl !== ""),
    [achievements]
  );

  const displayedImageAchievements = useMemo(() => {
    const itemsCount = achievementsPage * getItemsPerPage('achievements');
    return imageAchievements.slice(0, itemsCount);
  }, [imageAchievements, achievementsPage, getItemsPerPage]);

  // Check if there are more items to load
  const hasMoreArtworks = useMemo(() => 
    displayedArtworks.length < artworks.length,
    [displayedArtworks.length, artworks.length]
  );

  const hasMoreAchievements = useMemo(() => 
    displayedImageAchievements.length < imageAchievements.length,
    [displayedImageAchievements.length, imageAchievements.length]
  );

  // Reset pagination when tab changes (but not when editing)
  useEffect(() => {
    setArtworksPage(1);
    setAchievementsPage(1);
    setShowMoreLoading(false);
  }, [activeTab]);

  // Reset pagination on viewport change
  useEffect(() => {
    setArtworksPage(1);
    setAchievementsPage(1);
  }, [viewport.isMobile, viewport.isTablet]);

  useEffect(() => {
    const token = localStorage.getItem('artistToken');
    if (!token) {
      navigate('/artist-login');
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchData();
  }, [navigate]);

  const fetchData = async (preserveState = false) => {
    try {
      const [artworksRes, achievementsRes, genresRes, profileRes, exhibitionsRes, blogsRes] = await Promise.all([
        axios.get(`${API_URL}/artworks`),
        axios.get(`${API_URL}/achievements`),
        axios.get(`${API_URL}/genres`),
        axios.get(`${API_URL}/artist-profile`),
        axios.get(`${API_URL}/exhibitions`),
        axios.get(`${API_URL}/blogs/admin/all`)
      ]);

      setArtworks(artworksRes.data);
      setAchievements(achievementsRes.data);
      setGenres(genresRes.data);
      setProfile(profileRes.data);
      setProfileForm(profileRes.data);
      setExhibitions(exhibitionsRes.data);
      setBlogs(blogsRes.data);
      
      // Only reset pagination if not preserving state
      if (!preserveState) {
        setArtworksPage(1);
        setAchievementsPage(1);
      } else {
        // Restore saved pagination state
        setArtworksPage(savedPaginationRef.current.artworksPage);
        setAchievementsPage(savedPaginationRef.current.achievementsPage);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('artistToken');
        navigate('/artist-login');
      }
    } finally {
      setLoading(false);
      
      // Restore scroll position if preserving state
      if (preserveState) {
        setTimeout(() => {
          window.scrollTo({
            top: scrollPositionRef.current,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  };

  // Save current state before editing
  const saveCurrentState = useCallback(() => {
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    savedPaginationRef.current = {
      artworksPage,
      achievementsPage
    };
  }, [artworksPage, achievementsPage]);

  // Handle show more for artworks
  const handleShowMoreArtworks = useCallback(() => {
    setShowMoreLoading(true);
    setTimeout(() => {
      setArtworksPage(prev => prev + 1);
      setShowMoreLoading(false);
      
      // Smooth scroll to the first new item
      setTimeout(() => {
        const currentItems = artworksPage * getItemsPerPage('artworks');
        const artworkCards = document.querySelectorAll('.artwork-card01');
        if (artworkCards[currentItems]) {
          artworkCards[currentItems].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 100);
    }, 500);
  }, [artworksPage, getItemsPerPage]);

  // Handle show more for achievements
  const handleShowMoreAchievements = useCallback(() => {
    setShowMoreLoading(true);
    setTimeout(() => {
      setAchievementsPage(prev => prev + 1);
      setShowMoreLoading(false);
      
      // Smooth scroll to the first new item
      setTimeout(() => {
        const currentItems = achievementsPage * getItemsPerPage('achievements');
        const achievementCards = document.querySelectorAll('.achievement-card01');
        if (achievementCards[currentItems]) {
          achievementCards[currentItems].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 100);
    }, 500);
  }, [achievementsPage, getItemsPerPage]);

  const handleLogout = () => {
    localStorage.removeItem('artistToken');
    navigate('/');
  };

  const handleFileChange = (e, formType) => {
    const files = e.target.files;
    
    if (formType === 'artwork') {
      const newImages = Array.from(files);
      setArtworkForm({ 
        ...artworkForm, 
        images: [...artworkForm.images, ...newImages] 
      });
    } else if (formType === 'achievement') {
      setAchievementForm({ ...achievementForm, image: files[0] });
    } else if (formType === 'profile') {
      setProfileForm({ ...profileForm, photo: files[0] });
    }
  };

  const removeImage = (index) => {
    const newImages = artworkForm.images.filter((_, i) => i !== index);
    setArtworkForm({ ...artworkForm, images: newImages });
  };

  const handleArtworkSubmit = async (e) => {
    e.preventDefault();
    
    if (artworkForm.images.length === 0 && !editingItem) {
      alert('Please select at least one image');
      return;
    }

    try {
      const processedGenres = artworkForm.genres.length === 0 ? ['All'] : artworkForm.genres;

      if (editingItem) {
        // Save current state before updating
        saveCurrentState();
        
        let imageBase64 = '';
        
        if (artworkForm.images.length > 0) {
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(artworkForm.images[0]);
          });
        }

        const payload = {
          title: artworkForm.title,
          description: artworkForm.description || '',
          genres: processedGenres,
          imageBase64: imageBase64
        };

        try {
          const response = await axios.put(`${API_URL}/artworks/${editingItem.id}`, payload, {
            headers: { 'Content-Type': 'application/json' }
          });
          
          // Create updated artwork using form data (most reliable)
          let updatedArtwork;
          
          if (response.data && response.data.id) {
            // Backend returned the updated artwork
            updatedArtwork = {
              ...editingItem,
              ...response.data,
              // Ensure critical fields are present
              title: response.data.title || artworkForm.title,
              description: response.data.description || artworkForm.description || '',
              genres: Array.isArray(response.data.genres) ? response.data.genres : processedGenres,
              imageUrl: response.data.imageUrl || editingItem.imageUrl
            };
          } else {
            // Backend didn't return full data, use form data
            updatedArtwork = {
              ...editingItem,
              title: artworkForm.title,
              description: artworkForm.description || '',
              genres: processedGenres,
              imageUrl: editingItem.imageUrl // Keep existing image URL if no new image
            };
          }
          
          // Update the artwork in the state
          setArtworks(prevArtworks => 
            prevArtworks.map(artwork => 
              artwork.id === editingItem.id ? updatedArtwork : artwork
            )
          );
          
          alert('Artwork updated successfully!');
          setShowForm(false);
          setEditingItem(null);
          setArtworkForm({ title: '', description: '', genres: [], images: [] });
          
          // Restore scroll position after state update
          setTimeout(() => {
            window.scrollTo({
              top: scrollPositionRef.current,
              behavior: 'smooth'
            });
          }, 100);
        } catch (error) {
          console.error('Error updating artwork:', error);
          alert('Error updating artwork: ' + (error.response?.data?.message || error.message));
          // Don't close the form if there was an error
          return;
        }
      } else {
        const uploadPromises = artworkForm.images.map(async (image) => {
          const imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(image);
          });

          const payload = {
            title: artworkForm.title,
            description: artworkForm.description || '',
            genres: processedGenres,
            imageBase64: imageBase64
          };

          return axios.post(`${API_URL}/artworks`, payload, {
            headers: { 'Content-Type': 'application/json' }
          });
        });

        await Promise.all(uploadPromises);
        alert(`${artworkForm.images.length} artwork(s) uploaded successfully!`);
        setShowForm(false);
        setArtworkForm({ title: '', description: '', genres: [], images: [] });
        
        // Fetch data without preserving state for new uploads
        fetchData(false);
      }
    } catch (error) {
      console.error('Error saving artwork:', error);
      alert('Error saving artwork: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAchievementSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let imageBase64 = '';
      
      if (achievementForm.hasImage && achievementForm.image) {
        imageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(achievementForm.image);
        });
      }

      const payload = {
        title: achievementForm.title,
        description: achievementForm.description || '',
        imageBase64: imageBase64,
        hasImage: achievementForm.hasImage
      };

      await axios.post(`${API_URL}/achievements`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      alert('Achievement added successfully!');
      setShowForm(false);
      setAchievementForm({ title: '', description: '', image: null, hasImage: false });
      fetchData(false);
    } catch (error) {
      console.error('Error saving achievement:', error);
      alert('Error saving achievement: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let photoBase64 = '';
      
      if (profileForm.photo) {
        photoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(profileForm.photo);
        });
      }

      const payload = {
        name: profileForm.name,
        bio: profileForm.bio,
        designation: profileForm.designation,
        instagram: profileForm.instagram,
        facebook: profileForm.facebook,
        photoBase64: photoBase64
      };

      await axios.post(`${API_URL}/artist-profile`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      alert('Profile updated successfully!');
      fetchData(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile: ' + (error.response?.data?.message || error.message));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    try {
      await axios.post(`${API_URL}/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      alert('Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.message || 'Error changing password');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      await axios.delete(`${API_URL}/${type}s/${id}`);
      alert(`${type} deleted successfully!`);
      
      // Save state before deleting
      saveCurrentState();
      
      // Update state without full reload
      if (type === 'artwork') {
        setArtworks(prevArtworks => prevArtworks.filter(artwork => artwork.id !== id));
      } else if (type === 'achievement') {
        setAchievements(prevAchievements => prevAchievements.filter(achievement => achievement.id !== id));
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`Error deleting ${type}`);
    }
  };

  const handleEdit = (item, type) => {
    // Save current state before opening edit form
    saveCurrentState();
    
    setEditingItem(item);
    if (type === 'artwork') {
      // Ensure genres is an array and filter out 'All'
      const itemGenres = Array.isArray(item.genres) ? item.genres : [];
      setArtworkForm({
        title: item.title,
        description: item.description || '',
        genres: itemGenres.filter(g => g !== 'All'),
        images: []
      });
    }
    setShowForm(true);
  };

  const handleGenreToggle = (genre) => {
    const currentGenres = artworkForm.genres;
    if (currentGenres.includes(genre)) {
      setArtworkForm({
        ...artworkForm,
        genres: currentGenres.filter(g => g !== genre)
      });
    } else {
      setArtworkForm({
        ...artworkForm,
        genres: [...currentGenres, genre]
      });
    }
  };

  const addNewGenre = async () => {
    if (!newGenre.trim()) return;
    
    try {
      await axios.post(`${API_URL}/genres`, { genre: newGenre });
      setGenres([...genres, newGenre]);
      setNewGenre('');
      alert('Genre added successfully!');
    } catch (error) {
      console.error('Error adding genre:', error);
      alert('Error adding genre');
    }
  };

  const totalLikes = artworks.reduce((sum, artwork) => sum + (artwork.likes || 0), 0);
  const totalComments = artworks.reduce((sum, artwork) => sum + (artwork.comments?.length || 0), 0);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h3>Loading Dashboard...</h3>
          <p>Preparing your creative space</p>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-portfolio">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="/logo.png" alt="My Musing Logo" />
              <div>
                <h1>Artist Dashboard</h1>
                <p>Creative Management Portal</p>
              </div>
            </div>
            <div className="nav-linksad">
              <a href="/" className="nav-linkad">
                <Home size={16} />
                Home
              </a>
              <button onClick={() => setShowPasswordModal(true)} className="nav-linkad">
                <Key size={16} />
                Security
              </button>
              <button onClick={handleLogout} className="nav-linkad">
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container" style={{ padding: '35px 15px' }}>
        <div className="dashboard-tabs">
          {[
               { key: 'artworks', label: 'Artworks', icon: Image, count: artworks.length },
               { key: 'achievements', label: 'Achievements', icon: Award, count: achievements.length },
               { key: 'exhibitions', label: 'Exhibitions', icon: Calendar, count: exhibitions.length }, 
               { key: 'blog', label: 'Blog', icon: MessageCircle, count: blogs.length },
               { key: 'profile', label: 'Profile', icon: User, count: null }

          ].map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`dashboard-tab ${activeTab === key ? 'active' : ''}`}
            >
              <Icon size={20} />
              <span>{label}</span>
              {count !== null && <span className="tab-count">{count}</span>}
            </button>
          ))}
        </div>

        {activeTab === 'artworks' && (
          <div className="tab-content">
            <div className="tab-header">
              <div>
                <h2>Manage Artworks</h2>
                <p>Upload, edit, and organize your creative portfolio ({displayedArtworks.length} of {artworks.length})</p>
              </div>
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingItem(null);
                  setArtworkForm({ title: '', description: '', genres: [], images: [] });
                }}
                className="btn-primary01"
              >
                <Plus size={20} />
                Add New Artwork
              </button>
            </div>

            {artworks.length === 0 ? (
              <div className="no-items-dashboard">
                <Image size={64} />
                <h3>No Artworks Yet</h3>
                <p>Start building your portfolio by adding your first artwork</p>
              </div>
            ) : (
              <>
                <div className="artwork-grid01">
                  {displayedArtworks.map((artwork, index) => (
                    <div 
                      key={artwork.id} 
                      className="artwork-card01"
                      style={{
                        animationDelay: `${(index % getItemsPerPage('artworks')) * 0.05}s`
                      }}
                    >
                      <div className="artwork-image-wrapper01">
                        <img 
                          src={artwork.imageUrl} 
                          alt={artwork.title} 
                          className="artwork-image01"
                          loading={index < getItemsPerPage('artworks') ? 'eager' : 'lazy'}
                          decoding="async"
                        />
                        <div className="artwork-overlay01">
                         <div className="overlay-top">
                      <div>
                        <h3 className="overlay-title">{artwork.title}</h3>
                        <p className="overlay-description">{artwork.description}</p>
                      </div>
                    </div>
                    <div className="overlay-bottom">
                          <button
                            onClick={() => handleEdit(artwork, 'artwork')}
                            className="btn-secondary01"
                          >
                            <Edit size={16} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(artwork.id, 'artwork')}
                            className="overlay-delete-btn"
                          >
                        
                            Delete
                          </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {hasMoreArtworks && (
                  <div className="show-more-container">
                    <div className="pagination-info">
                      Showing {displayedArtworks.length} of {artworks.length} artworks
                    </div>
                    <button 
                      className="show-more-btn"
                      onClick={handleShowMoreArtworks}
                      disabled={showMoreLoading}
                      aria-label={`Load ${getItemsPerPage('artworks')} more artworks`}
                      type="button"
                    >
                      {showMoreLoading ? (
                        <>
                          <Loader className="spinner" size={20} />
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <Plus size={20} />
                          <span>Load More ({Math.min(getItemsPerPage('artworks'), artworks.length - displayedArtworks.length)} items)</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="tab-content">
            <div className="tab-header">
              <div>
                <h2>Manage Achievements</h2>
                <p>Showcase your artistic milestones and recognition</p>
              </div>
              <button
                onClick={() => {
                  setShowForm(true);
                  setAchievementForm({ title: '', description: '', image: null, hasImage: false });
                }}
                className="btn-primary01"
              >
                <Plus size={20} />
                Add Achievement
              </button>
            </div>

            {/* Text-Only Achievements - Always show all */}
            {textAchievements.length > 0 && (
              <div className="text-achievements-section">
                <div className="section-subtitle-dashboard">
                  <Award size={20} />
                  <span>Text Achievements ({textAchievements.length})</span>
                </div>
                <div className="text-achievements-grid-dashboard">
                  {textAchievements.map((achievement, index) => (
                    <div 
                      key={achievement.id} 
                      className="text-achievement-card-dashboard"
                      style={{
                        animationDelay: `${index * 0.05}s`
                      }}
                    >
                      <div className="text-achievement-icon-dashboard">
                        <Award size={24} />
                      </div>
                      <div className="text-achievement-content-dashboard">
                        <h3 className="text-achievement-title-dashboard">{achievement.title}</h3>
                        {achievement.description && (
                          <p className="text-achievement-description-dashboard">{achievement.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(achievement.id, 'achievement')}
                        className="delete-achievement-btn"
                        aria-label="Delete achievement"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Image-Based Achievements - With pagination */}
            {imageAchievements.length > 0 && (
              <div className="image-achievements-section">
                <div className="section-subtitle-dashboard">
                  <Image size={20} />
                  <span>Image Achievements ({displayedImageAchievements.length} of {imageAchievements.length})</span>
                </div>
                <div className="achievements-grid01">
                  {displayedImageAchievements.map((achievement, index) => (
                    <div 
                      key={achievement.id} 
                      className="achievement-card01"
                      style={{
                        animationDelay: `${(index % getItemsPerPage('achievements')) * 0.1}s`
                      }}
                    >
                      {achievement.imageUrl && (
                        <div className="achievement-image-container01">
                          <img 
                            src={achievement.imageUrl} 
                            alt={achievement.title} 
                            className="achievement-image01"
                            loading={index < getItemsPerPage('achievements') ? 'eager' : 'lazy'}
                            decoding="async"
                          />
                        </div>
                      )}
                      <div className="achievement-info01">
                        <div className="achievement-header01">
                          <Award className="achievement-icon01" size={24} />
                          <h3 className="achievement-title01">{achievement.title}</h3>
                        </div>
                        <div className="achievement-actions01">
                          <button
                            onClick={() => handleDelete(achievement.id, 'achievement')}
                            className="btn-danger01"
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {hasMoreAchievements && (
                  <div className="show-more-container">
                    <div className="pagination-info">
                      Showing {displayedImageAchievements.length} of {imageAchievements.length} image achievements
                    </div>
                    <button 
                      className="show-more-btn"
                      onClick={handleShowMoreAchievements}
                      disabled={showMoreLoading}
                      aria-label={`Load ${getItemsPerPage('achievements')} more achievements`}
                      type="button"
                    >
                      {showMoreLoading ? (
                        <>
                          <Loader className="spinner" size={20} />
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <Plus size={20} />
                          <span>Load More ({Math.min(getItemsPerPage('achievements'), imageAchievements.length - displayedImageAchievements.length)} items)</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {achievements.length === 0 && (
              <div className="no-items-dashboard">
                <Award size={64} />
                <h3>No Achievements Yet</h3>
                <p>Start adding your artistic milestones and recognition</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="tab-content">
            <div className="tab-header">
              <div>
                <h2>Update Profile</h2>
                <p>Manage your artist profile and social media links</p>
              </div>
            </div>
            
            <div className="profile-container">
              <div className="profile-preview">
                <div className="profile-image-container">
                  {profile.photo ? (
                    <img src={profile.photo} alt="Profile" className="profile-image" />
                  ) : (
                    <div className="profile-placeholder">
                      <User size={48} />
                    </div>
                  )}
                </div>
                <div className="profile-details">
                  <h3>{profile.name || 'Your Name'}</h3>
                  <p className="profile-designation">{profile.designation || 'Artist'}</p>
                  <p className="profile-bio">{profile.bio || 'Add your bio here...'}</p>
                </div>
              </div>

              <div className="profile-form">
                <form onSubmit={handleProfileSubmit}>
                  <div className="form-group">
                    <label>Profile Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'profile')}
                      className="file-input01"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        required
                        value={profileForm.name}
                        className="text-input01"
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Designation</label>
                      <input
                        type="text"
                        value={profileForm.designation}
                        className="text-input01"
                        onChange={(e) => setProfileForm({ ...profileForm, designation: e.target.value })}
                        placeholder="Artist, Painter, Designer..."
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Bio</label>
                    <textarea
                      rows="4"
                      value={profileForm.bio}
                      className="text-input01"
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      placeholder="Tell visitors about yourself and your artistic journey..."
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Instagram URL</label>
                      <input
                        type="url"
                        value={profileForm.instagram}
                        className="text-input01"
                        onChange={(e) => setProfileForm({ ...profileForm, instagram: e.target.value })}
                        placeholder="https://instagram.com/yourusername"
                      />
                    </div>

                    <div className="form-group">
                      <label>Facebook URL</label>
                      <input
                        type="url"
                        value={profileForm.facebook}
                        className="text-input01"
                        onChange={(e) => setProfileForm({ ...profileForm, facebook: e.target.value })}
                        placeholder="https://facebook.com/yourusername"
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-primary">
                    <Save size={20} />
                    Update Profile
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Artwork Form Modal */}
      {showForm && activeTab === 'artworks' && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowForm(false)}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>{editingItem ? 'Edit Artwork' : 'Add New Artwork'}</h3>
              <p>Share your creative vision with the world</p>
            </div>
            
            <form onSubmit={handleArtworkSubmit} className="modal-form">
              <div className="form-group">
                <label>Artwork Images *</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  required={!editingItem && artworkForm.images.length === 0}
                  onChange={(e) => handleFileChange(e, 'artwork')}
                  className="file-input"
                />
                <small>Select multiple images to upload at once</small>
              </div>

              {artworkForm.images.length > 0 && (
                <div className="form-group">
                  <label>Selected Images ({artworkForm.images.length})</label>
                  <div className="image-preview-grid">
                    {artworkForm.images.map((image, index) => (
                      <div key={index} className="image-preview-item">
                        <img 
                          src={URL.createObjectURL(image)} 
                          alt={`Preview ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeImage(index)}
                          aria-label="Remove image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Title *</label>
                <input
                  className='text-input'
                  type="text"
                  required
                  value={artworkForm.title}
                  onChange={(e) => setArtworkForm({ ...artworkForm, title: e.target.value })}
                  placeholder="Enter artwork title *"
                />
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  rows="4"
                  className='text-input'
                  value={artworkForm.description}
                  onChange={(e) => setArtworkForm({ ...artworkForm, description: e.target.value })}
                  placeholder="Describe your artwork..."
                />
              </div>

              <div className="form-group">
                <label>Genres (Optional - defaults to "All")</label>
                <div className="genre-selection">
                  {genres.filter(g => g !== 'All').map(genre => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => handleGenreToggle(genre)}
                      className={`genre-btn ${artworkForm.genres.includes(genre) ? 'selected' : ''}`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
                
                <div className="add-genre">
                  <input
                    className='text-input'
                    type="text"
                    placeholder="Add new genre"
                    value={newGenre}
                    onChange={(e) => setNewGenre(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={addNewGenre}
                    className="btn-secondary"
                  >
                    Add Genre
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Save size={20} />
                  {editingItem ? 'Update' : `Upload ${artworkForm.images.length || 0}`} Artwork{artworkForm.images.length > 1 ? 's' : ''}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  <X size={20} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Achievement Form Modal */}
      {showForm && activeTab === 'achievements' && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowForm(false)}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Add New Achievement</h3>
              <p>Celebrate your artistic milestones</p>
            </div>
            
            <form onSubmit={handleAchievementSubmit} className="modal-form">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={achievementForm.hasImage}
                    onChange={(e) => setAchievementForm({ ...achievementForm, hasImage: e.target.checked, image: e.target.checked ? achievementForm.image : null })}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span className='text01'>This achievement has an image</span>
                </label>
              </div>

              {achievementForm.hasImage && (
                <div className="form-group">
                  <label className='label01'>Achievement Image *</label>
                  <input
                    type="file"
                    accept="image/*"
                    required
                    onChange={(e) => handleFileChange(e, 'achievement')}
                    className="file-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label className='label01'>Title *</label>
                <input
                  className='text-input'
                  type="text"
                  required
                  value={achievementForm.title}
                  onChange={(e) => setAchievementForm({ ...achievementForm, title: e.target.value })}
                  placeholder="Achievement title *"
                />
              </div>

              <div className="form-group">
                <label className='label01'>Description (Optional)</label>
                <textarea
                  className='text-input'
                  rows="4"
                  value={achievementForm.description}
                  onChange={(e) => setAchievementForm({ ...achievementForm, description: e.target.value })}
                  placeholder="Describe your achievement, award, or recognition..."
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Save size={20} />
                  Add Achievement
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  <X size={20} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
{activeTab === 'exhibitions' && (
  <ArtistExhibition
    exhibitions={exhibitions}
    fetchData={fetchData}
    viewport={viewport}
  />
)}
{activeTab === 'blog' && (
  <ArtistBlog
    blogs={blogs}
    fetchData={fetchData}
    viewport={viewport}
  />
)}
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPasswordModal(false)}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Change Password</h3>
              <p>Update your account security</p>
            </div>
            
            <form onSubmit={handlePasswordChange} className="modal-form">
              <div className="form-group">
                <label>Current Password *</label>
                <input
                  className='text-input'
                  type="password"
                  required
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-group">
                <label>New Password *</label>
                <input
                  className='text-input'
                  type="password"
                  required
                  minLength="6"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password"
                />
                <small>Password must be at least 6 characters long</small>
              </div>

              <div className="form-group">
                <label>Confirm New Password *</label>
                <input
                  className='text-input'
                  type="password"
                  required
                  minLength="6"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Key size={20} />
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="btn-secondary"
                >
                  <X size={20} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistDashboard;